import { useEffect, useState } from "react";
import {
  getAssistantCreditSettings,
  updateAssistantCreditSettings,
} from "@/api/assistant";
import type { AssistantCreditSettings } from "@sales-ops-amplify/shared";

export function CreditSettingsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [settings, setSettings] = useState<AssistantCreditSettings>({
    mode: "threshold",
    thresholdCredits: 1,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    void getAssistantCreditSettings().then((r) => setSettings(r.settings));
  }, [open]);

  if (!open) return null;

  async function save() {
    setSaving(true);
    try {
      await updateAssistantCreditSettings(settings);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-foreground">
          Assistant credit spend limit
        </h2>
        <div className="mt-4 space-y-3">
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="credit-mode"
              className="mt-1"
              checked={settings.mode === "always_ask"}
              onChange={() =>
                setSettings((s) => ({ ...s, mode: "always_ask" }))
              }
            />
            <span>Always ask before using credits</span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              type="radio"
              name="credit-mode"
              className="mt-1"
              checked={settings.mode === "threshold"}
              onChange={() =>
                setSettings((s) => ({ ...s, mode: "threshold" }))
              }
            />
            <span className="flex flex-wrap items-center gap-2">
              Only ask when credit cost exceeds
              <input
                type="number"
                min={1}
                className="w-16 rounded border border-border bg-background px-2 py-1 text-sm"
                value={settings.thresholdCredits}
                disabled={settings.mode !== "threshold"}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    thresholdCredits: Number(e.target.value) || 1,
                  }))
                }
              />
              credits
            </span>
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/30"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-60"
            disabled={saving}
            onClick={() => void save()}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
